import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  Fragment,
} from "react";
import { Rings } from "react-loader-spinner";
import EV54DashboardMobileView from "./MobileViews/EV54DashboardMobileView";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import EV54DashboardController from "../EV54Dashboard/EV54DashboardController";

import ScriptsByHourGraph from "./graphs/ScriptsByHourGraph";
import ScriptTypeGraph from "./graphs/ScriptTypeGraph";
import CanisterUtilizationGraph from "./graphs/CanisterUtilizationGraph";
import EV54AlarmsTable from "./SubViews/EV54AlarmsTable";
import EV54CanisterDetailsTable from "./SubViews/EV54CanisterDetailsTable";
import EV54InventoryTable from "./SubViews/EV54InventoryTable";
import BarGraph from "../sharedGraphs/BarGraph";
import { ScreenContext } from "../../../contexts/ScreenContext";
import { useMediaQuery } from "react-responsive";
import styles from "./EV54Dashboard.module.css";
import stylesMobile from "./MobileViews/EV54DashboardMobileView.module.css";

const soonToExpireThreshold = 90;

const EV54Dashboard = ({ props }) => {
  const {
    user,
    selectedSite,
    dashboardView,
    windowHeight,
    dashboardReturnStateLocal,
    makeDashboardReturnStateLocalNull,
    onShowDropdowns,
  } = props;

  const { t } = useTranslation("truscriptDashboard");

  const [fetchingScriptsByHour, setFetchingScriptsByHour] = useState(false);
  const [fetchingScriptType, setFetchingScriptType] = useState(false);
  const [fetchingCanisterUtilization, setFetchingCanisterUtilization] =
    useState(false);
  const [fetchingAlarms, setFetchingAlarms] = useState(false);
  const [fetchingInventory, setFetchingInventory] = useState(false);
  const [selectedGraph, setSelectedGraph] = useState(null);
  const [SelectedGraphView, setSelectedGraphView] = useState(null);
  const [scriptsByHourGraphData, setScriptsByHourGraphData] = useState([]);
  const [scriptsByHourRollingAverage, setScriptsByHourRollingAverage] =
    useState(null);
  const [scriptTypesGraphData, setScriptTypesGraphData] = useState([]);
  const [canisterUtilizationGraphData, setCanisterUtilizationGraphData] =
    useState([]);
  const [alarmsGraphData, setAlarmsGraphData] = useState([]);
  const [inventoryGraphData, setInventoryGraphData] = useState([]);
  const [inventoryTableData, setInventoryTableData] = useState([]);
  const [stopDate] = useState(new Date());
  const [alarmsStartDate] = useState(() => {
    let startDate = new Date();
    startDate.setDate(startDate.getDate() - 6); // 7 days total
    return startDate;
  });

  const viewHeight = (+windowHeight - 200).toString() + "px";
  const inventoryStartDate = new Date();
  inventoryStartDate.setDate(inventoryStartDate.getDate() - 4); // 5 days total

  const _controllerRef = useRef(EV54DashboardController);

  const navigate = useNavigate();

  const { smallScreen } = useContext(ScreenContext);
  const smScreen = useMediaQuery(smallScreen);

  const getDateRange = (dayCount) => {
    const dt = new Date();
    dt.setHours(23, 59, 59); // set always to the end of TODAY
    const stopDate = new Date(dt.getTime());
    dt.setDate(dt.getDate() - dayCount);
    const startDate = new Date(dt.getTime());
    return { startDate, stopDate };
  };

  useEffect(() => {
    selectedGraph ? onShowDropdowns(false) : onShowDropdowns(true);
  }, [selectedGraph, onShowDropdowns]);

  useEffect(() => {
    setScriptsByHourGraphData([]);
    setScriptsByHourRollingAverage(null);
    setScriptTypesGraphData([]);
    setCanisterUtilizationGraphData([]);
    setAlarmsGraphData([]);
    setInventoryGraphData([]);
    setInventoryTableData([]);
  }, [selectedSite]);

  useEffect(() => {
    // Used after navigation return from Devices to EV54CanisterDetailsTable
    if (
      dashboardReturnStateLocal &&
      dashboardReturnStateLocal.source === "EV54CanisterDetailsTable" &&
      canisterUtilizationGraphData.length
    ) {
      setSelectedGraph(dashboardReturnStateLocal?.selectedGraph);
      makeDashboardReturnStateLocalNull(null);
    }
  }, [canisterUtilizationGraphData]);

  useEffect(() => {
    // Used after navigation return from Devices to EV54CanisterDetailsTable
    if (
      dashboardReturnStateLocal &&
      dashboardReturnStateLocal.source === "EV54InventoryTable" &&
      inventoryTableData.length
    ) {
      setSelectedGraph(dashboardReturnStateLocal?.selectedGraph);
      makeDashboardReturnStateLocalNull(null);
    }
  }, [inventoryTableData]);

  /***********************************************************************  
  Implement graph-data fetching methods here
  ***********************************************************************/
  useEffect(() => {
    // Get initial data for Scripts by Hour graph
    const { startDate, stopDate } = getDateRange(1);

    const utcStartDate = startDate.toUTCString();
    const utcStopDate = stopDate.toUTCString();

    setScriptsByHourGraphData([]);
    setScriptsByHourRollingAverage(null);
    setFetchingScriptsByHour(true);
    _controllerRef.current
      .getEV54ScriptsPerHourData(
        user,
        selectedSite,
        null,
        utcStartDate,
        utcStopDate
      )
      .then((data) => {
        const getScriptsForHour = (hour, prevDayResults) => {
          return prevDayResults.filter((o) => {
            const dt = new Date(o.processedOn);
            return dt.getHours() === hour;
          });
        };

        let hourCountData = [];
        for (let hour = 0; hour < 24; hour++) {
          const temp = getScriptsForHour(hour, data.results.prevDayResults);
          hourCountData.push({
            hour: hour,
            count: temp.length,
          });
        }
        if (data?.results?.prevDayResults?.length) {
          setScriptsByHourGraphData(
            hourCountData.map((result, index) => ({
              x:
                index < 12
                  ? `${result.hour > 0 ? result.hour : 12} AM`
                  : `${result.hour > 12 ? result.hour - 12 : 12} PM`,
              y: result.count,
            }))
          );
        } else {
          setScriptsByHourGraphData([]);
        }

        setScriptsByHourRollingAverage(
          data?.results?.rollingAverage ? data.results.rollingAverage : 0
        );

        setFetchingScriptsByHour(false);
      })
      .catch((error) => {
        console.log(error);
        setFetchingScriptsByHour(false);
      });
  }, [selectedSite, user]);

  useEffect(() => {
    // Get initial data for Script Type graph
    const { startDate, stopDate } = getDateRange(1);
    const utcStartDate = startDate.toUTCString();
    const utcStopDate = stopDate.toUTCString();

    setScriptTypesGraphData([]);
    setFetchingScriptType(true);
    _controllerRef.current
      .getEV54ScriptTypes(user, selectedSite, null, utcStartDate, utcStopDate)
      .then((results) => {
        if (results && results?.results && results.results.length) {
          let highNumScripts = 0;
          let lowNumScripts = 0;
          let statNumScripts = 0;
          let waiterNumScripts = 0;

          results.results.forEach((result) => {
            if (result.scriptType === "HIGH") {
              highNumScripts += 1;
            } else if (result.scriptType === "LOW") {
              lowNumScripts += 1;
            } else if (result.scriptType === "STAT") {
              statNumScripts += 1;
            } else if (result.scriptType === "WAITER") {
              waiterNumScripts += 1;
            }
          });
          setScriptTypesGraphData([
            { x: t("highLabel"), y: highNumScripts },
            { x: t("lowLabel"), y: lowNumScripts },
            { x: t("statLabel"), y: statNumScripts },
            { x: t("waiterLabel"), y: waiterNumScripts },
          ]);
        } else {
          setScriptTypesGraphData([]);
        }

        setFetchingScriptType(false);
      })
      .catch((error) => {
        console.log(error);
        setFetchingScriptType(false);
      });
  }, [selectedSite, user, t]);

  useEffect(() => {
    // Get initial data for Canister Utilization graph
    setCanisterUtilizationGraphData([]);
    setFetchingCanisterUtilization(true);
    _controllerRef.current
      .getEV54CanisterUtilization(user, selectedSite, null)
      .then((results) => {
        if (results && results?.results && results.results?.length) {
          // console.log('results.results[0]: ', results.results[0]);
          const canisters = results.results[0].canisters;
          let tempCanisterUtilizationGraphData = canisters.map((canister) => ({
            x: `${canister.deviceName}\n${canister._id}`,
            y: canister.scripts,
            y1: results.results[0].averageScripts,
            device: canister.deviceName, // stock location
            canister: canister._id,
            product: canister.product,
          }));

          function compare(a, b) {
            const countA = a.y;
            const countB = b.y;

            let comparison = 0;
            if (countA > countB) {
              comparison = 1;
            } else if (countA < countB) {
              comparison = -1;
            }
            return comparison;
          }

          tempCanisterUtilizationGraphData.sort(compare);
          setCanisterUtilizationGraphData(tempCanisterUtilizationGraphData);
        }
        setFetchingCanisterUtilization(false);
      })

      .catch((error) => {
        console.log(error);
        setFetchingCanisterUtilization(false);
      });
  }, [selectedSite, user]);

  /************ Get data for Alarms graph ***********/
  useEffect(() => {
    setAlarmsGraphData([]);
    setFetchingAlarms(true);
    _controllerRef.current
      .getEV54AlarmHistory(user, selectedSite, alarmsStartDate, stopDate)
      .then((results) => {
        // console.log("results: ", results);
        const alarmsData = results.map((result) => {
          return { x: result.error, y: result.count };
        });

        let alarmTypes = [];
        let accumulatedAlarmData = [];

        for (let obj of alarmsData) {
          if (!alarmTypes.includes(obj.x)) {
            alarmTypes.push(obj.x);
            accumulatedAlarmData.push({ x: obj.x, y: obj.y });
          } else {
            const foundObj = accumulatedAlarmData.find(
              (objInErrorData) => objInErrorData.x === obj.x
            );
            if (foundObj) {
              foundObj.y += obj.y;
            }
          }
        }
        setAlarmsGraphData(accumulatedAlarmData);
        setFetchingAlarms(false);
      })
      .catch((error) => {
        console.log(error);
        setFetchingAlarms(false);
      });
  }, [user, selectedSite, alarmsStartDate, stopDate]);

  useEffect(() => {
    // Get initial data for Inventory graph
    setInventoryGraphData([]);
    setFetchingInventory(true);
    _controllerRef.current
      .getEV54Inventory(user, selectedSite)
      .then((results) => {
        // console.log("results: ", results);
        let belowMin = 0;
        let expired = 0;
        let overMax = 0;
        let soonToExpire = 0;
        let today = new Date();
        let soonToExpireDate = new Date(today);
        soonToExpireDate.setDate(
          soonToExpireDate.getDate() + soonToExpireThreshold
        );

        results.results.forEach((result) => {
          if (result.quantity < result.min) belowMin++;
          if (result.quantity > result.max) overMax++;
          const isExpired = Date.parse(result.expiration) <= today;
          if (isExpired) expired++;
          if (!isExpired && Date.parse(result.expiration) <= soonToExpireDate)
            soonToExpire++;
        });
        if (results?.results && results.results.length) {
          setInventoryGraphData([
            { x: t("belowLabel"), y: belowMin },
            { x: t("overLabel"), y: overMax },
            { x: t("expiredLabel"), y: expired },
            { x: t("soonToExpireLabel"), y: soonToExpire },
          ]);
        } else {
          setInventoryGraphData([]);
        }

        function compare(a, b) {
          const canisterA = Number(a.canister.name.split("-")[2]);
          const canisterB = Number(b.canister.name.split("-")[2]);

          let comparison = 0;
          if (canisterA > canisterB) {
            comparison = 1;
          } else if (canisterA < canisterB) {
            comparison = -1;
          }
          return comparison;
        }

        let tempTableDataSorted = results.results.filter(
          (result) =>
            result.quantity < result.min || result.quantity > result.max
        );
        tempTableDataSorted.sort(compare);

        setInventoryTableData(tempTableDataSorted);
        setFetchingInventory(false);
      })
      .catch((error) => {
        console.log(error);
        setFetchingInventory(false);
      });
  }, [selectedSite, user, t]);
  /***********************************************************************/

  useEffect(() => {
    switch (selectedGraph) {
      case 1:
        setSelectedGraphView();
        navigate("/reports", {
          state: {
            returnURL: "/dashboard",
            reportName: "Production",
            dataFromDashboard: {
              returnURL: "/dashboard",
              returnView: dashboardView,
              reportName: "Production",
              selectedSite:
                typeof selectedSite === "object" ? selectedSite : null,
            },
          },
        });
        break;
      case 2:
        setSelectedGraphView();
        navigate("/reports", {
          state: {
            returnURL: "/dashboard",
            reportName: "Production",
            dataFromDashboard: {
              returnURL: "/dashboard",
              returnView: dashboardView,
              reportName: "Production",
              selectedSite:
                typeof selectedSite === "object" ? selectedSite : null,
            },
          },
        });
        break;
      case 3:
        setSelectedGraphView(
          <EV54CanisterDetailsTable
            tableData={canisterUtilizationGraphData}
            goBackHandler={goBackHandler}
            selectedSite={!selectedSite ? "All Sites" : selectedSite}
            selectedGraph={selectedGraph}
            windowHeight={windowHeight}
          />
        );
        break;
      case 4:
        setSelectedGraphView(
          <EV54AlarmsTable
            goBackHandler={goBackHandler}
            selectedSite={!selectedSite ? "All Sites" : selectedSite}
            deviceName={selectedGraph?.deviceName}
            _controllerRef={_controllerRef}
            user={user}
            startDate={alarmsStartDate}
            stopDate={stopDate}
            windowHeight={windowHeight}
          />
        );
        break;
      case 5:
        setSelectedGraphView(
          <EV54InventoryTable
            goBackHandler={goBackHandler}
            selectedSite={!selectedSite ? "All Sites" : selectedSite}
            selectedGraph={selectedGraph}
            windowHeight={windowHeight}
            inventoryTableData={inventoryTableData}
          />
        );
        break;
      default:
        setSelectedGraphView(null);
    }
  }, [selectedGraph, selectedSite]);

  const goBackHandler = () => {
    setSelectedGraph(null);
  };

  const handleGraphClick = (graphId) => {
    setSelectedGraph(graphId);
  };

  const loader = (
    <div
      className={
        smScreen
          ? stylesMobile.EV54DashboardMobile__loader
          : styles.EV54Dashboard__loader
      }
    >
      <Rings color="#089BAB" height={110} width={110} />
      <p>{t("fetching")}</p>
    </div>
  );

  const ScriptsByHourGraphContainer = (
    <div
      key={1}
      className={
        smScreen
          ? `${stylesMobile.EV54Dashboard__graphContainerMobile} ${stylesMobile["EV54Dashboard__graphContainerMobile--paddingBottom"]}`
          : null
      }
      onClick={!fetchingScriptsByHour ? () => handleGraphClick(1) : null}
    >
      {fetchingScriptsByHour && loader}
      <ScriptsByHourGraph
        user={user}
        fetchingData={fetchingScriptsByHour}
        graphData={scriptsByHourGraphData}
        benchmarkValue={scriptsByHourRollingAverage}
        graphTitle={t("hourlyTitle")}
        palette={["#318ce7", "#e32636"]}
        primarySeriesLabel={t("filledLabel")}
        secondarySeriesLabel={t("benchmarkLabel")}
        titleXPosition={166}
      />
    </div>
  );

  const ScriptTypeGraphContainer = (
    <div
      key={2}
      className={
        smScreen
          ? `${stylesMobile.EV54Dashboard__graphContainerMobile} ${stylesMobile["EV54Dashboard__graphContainerMobile--paddingBottom"]}`
          : null
      }
      onClick={!fetchingScriptType ? () => handleGraphClick(2) : null}
    >
      {fetchingScriptType && loader}
      <ScriptTypeGraph
        user={user}
        fetchingData={fetchingScriptType}
        graphData={scriptTypesGraphData}
        graphTitle={t("typesTitle")}
        palette={["#318ce7", "#FC8A17", "#C0C0C0", "#ffdf00"]}
      />
    </div>
  );

  const CanisterUtilizationGraphContainer = (
    <div
      key={3}
      className={
        smScreen
          ? `${stylesMobile.EV54Dashboard__graphContainerMobile} ${stylesMobile["EV54Dashboard__graphContainerMobile--paddingBottom"]}`
          : null
      }
      onClick={!fetchingCanisterUtilization ? () => handleGraphClick(3) : null}
    >
      {fetchingCanisterUtilization && loader}
      <CanisterUtilizationGraph
        user={user}
        fetchingData={fetchingCanisterUtilization}
        language={user?.language?.type}
        graphData={canisterUtilizationGraphData.slice(0, 6)}
        graphTitle={t("canisterTitle")}
        palette={["#318ce7", "#e32636"]}
        primarySeriesLabel={t("quantityLabel")}
        secondarySeriesLabel={t("averageLabel")}
      />
    </div>
  );
  const AlarmsGraphContainer = (
    <div
      key={4}
      className={
        smScreen
          ? `${stylesMobile.EV54Dashboard__graphContainerMobile} ${stylesMobile["EV54Dashboard__graphContainerMobile--paddingBottom"]}`
          : null
      }
      onClick={!fetchingAlarms ? () => handleGraphClick(4) : null}
    >
      {fetchingAlarms && loader}
      <BarGraph
        user={user}
        fetchingData={fetchingAlarms}
        stylesName="BarGraph1"
        chartWidth={450}
        chartHeight={288}
        graphData={alarmsGraphData}
        graphTitle={t("alarmsTitle")}
        palette={["#318ce7"]}
        titleXPosition={202}
      />
    </div>
  );

  const InventoryGraphContainer = (
    <div
      key={5}
      className={
        smScreen
          ? `${stylesMobile.EV54Dashboard__graphContainerMobile} ${stylesMobile["EV54Dashboard__graphContainerMobile--paddingBottom"]}`
          : null
      }
      onClick={!fetchingInventory ? () => handleGraphClick(5) : null}
    >
      {fetchingInventory && loader}
      <BarGraph
        user={user}
        fetchingData={fetchingInventory}
        stylesName="BarGraph1"
        chartWidth={450}
        chartHeight={288}
        graphData={inventoryGraphData}
        graphTitle={t("inventoryTitle")}
        palette={["#318ce7"]}
        titleXPosition={193}
      />
    </div>
  );
  const Graphs = [
    ScriptsByHourGraphContainer,
    ScriptTypeGraphContainer,
    CanisterUtilizationGraphContainer,
    AlarmsGraphContainer,
    InventoryGraphContainer,
  ];

  return (
    <Fragment>
      {!selectedGraph && !smScreen && (
        <div
          style={{ height: viewHeight }}
          className={styles.EV54Dashboard__graphsGroup}
        >
          {Graphs}
        </div>
      )}
      {!selectedGraph && smScreen && (
        <div className={stylesMobile.EV54Dashboard__graphsGroupMobile}>
          <EV54DashboardMobileView Graphs={Graphs} />
        </div>
      )}

      {selectedGraph && SelectedGraphView}
    </Fragment>
  );
};

export default EV54Dashboard;
